import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarResults,
  KBarSearch,
  VisualState,
  useKBar,
  useMatches as useKBarMatches,
} from "kbar";
import { PropsWithChildren, useEffect, useState } from "react";

import LoadingSpinner from "@/components/elements/LoadingSpinner";
import classNames from "@/helpers/classNames";
import LoadingProvider, {
  useLoadingContext,
} from "@/providers/LoadingProvider";
import { ChevronRightIcon } from "lucide-react";

import { useConnectionActions } from "./actions/useConnectionActions";
import { useDataSourceSyncActions } from "./actions/useDataSourceSyncActions";
import useModelToolActions from "./actions/useModelToolActions";
import { useReverseEtlSyncActions } from "./actions/useReverseEtlSyncActions";
import useSystemActions from "./actions/useSystemActions";

function RenderResults() {
  const { results } = useKBarMatches();
  const { loading } = useLoadingContext();
  return (
    <>
      {loading && (
        <div className="pointer-events-none flex items-center gap-4 px-4 text-muted-foreground">
          <LoadingSpinner className="h-4" />
          <span>Loading ...</span>
        </div>
      )}
      <KBarResults
        items={results}
        onRender={({ item, active }) =>
          typeof item === "string" ? (
            item ? (
              <div className="bg-black/10 px-4 py-2 text-xs text-muted-foreground uppercase dark:bg-white/5">
                {item}
              </div>
            ) : (
              <div />
            )
          ) : (
            <div
              className={classNames(
                "px-4 py-3",
                active
                  ? "bg-gray-100 dark:bg-gray-800"
                  : "bg-background dark:bg-gray-900",
              )}
            >
              <div className="flex items-center space-x-2">
                {item.icon && <div>{item.icon}</div>}
                <div>
                  <div>
                    <div className="flex items-center space-x-1">
                      {item.parent && (
                        <div className="flex items-center space-x-1 text-muted-foreground">
                          <span>{item.parentActionImpl.name}</span>
                          <ChevronRightIcon className="w-3" />
                        </div>
                      )}

                      <div>{item.name}</div>
                    </div>
                  </div>
                  {item.subtitle && (
                    <div className="text-xs text-gray-400">{item.subtitle}</div>
                  )}
                </div>
              </div>
            </div>
          )
        }
      />
    </>
  );
}

export function KBar(props: PropsWithChildren) {
  return (
    <KBarProvider options={{ enableHistory: true }}>
      <LoadingProvider>
        <KBarContent {...props} />
      </LoadingProvider>
    </KBarProvider>
  );
}

/**
 * Will return true once the KBar has been opened and will continue to return to true thereafter
 */
function useIsReadyToLoadData() {
  const { isKBarOpen } = useKBar((state) => ({
    isKBarOpen: state.visualState === VisualState.showing,
  }));
  const [isReady, setIsReady] = useState(isKBarOpen);
  useEffect(() => {
    if (isKBarOpen) {
      setIsReady(true);
    }
  }, [isKBarOpen]);
  return isReady;
}

export function KBarContent(props: PropsWithChildren) {
  const isReadyToLoadData = useIsReadyToLoadData();
  useSystemActions();
  useModelToolActions();

  const registerDataSourceSyncActions = useDataSourceSyncActions();
  const registerReverseEtlSyncActions = useReverseEtlSyncActions();
  const registerConnectionActions = useConnectionActions();

  useEffect(() => {
    if (isReadyToLoadData) {
      registerDataSourceSyncActions();
      registerReverseEtlSyncActions();
      registerConnectionActions();
    }
  }, [
    registerDataSourceSyncActions,
    registerReverseEtlSyncActions,
    registerConnectionActions,
    isReadyToLoadData,
  ]);

  return (
    <>
      <KBarPortal>
        <KBarPositioner className="z-50">
          <KBarAnimator>
            <div
              className="overflow-hidden rounded-xl border bg-background dark:bg-gray-900"
              style={{
                boxShadow: "0px 6px 20px rgb(0 0 0 / 20%)",
              }}
            >
              <KBarSearch className="h-12 w-screen max-w-2xl bg-background px-4 focus:outline-hidden dark:bg-gray-900" />

              <RenderResults />
            </div>
          </KBarAnimator>
        </KBarPositioner>
      </KBarPortal>
      {props.children}
    </>
  );
}
